import { useEffect, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { clearAccountingClientBookkeeping } from '@/modules/accounting/bookkeeping/accountingBookkeepingService'

interface ClearBookkeepingModalProps {
  client: AccountingClient | null
  isOpen: boolean
  onClose: () => void
  onCleared: () => void
}

export function ClearBookkeepingModal({
  client,
  isOpen,
  onClose,
  onCleared,
}: ClearBookkeepingModalProps) {
  const { t } = useTranslation()
  const [clearing, setClearing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) setError(null)
  }, [isOpen])

  const handleClear = async () => {
    if (!client || clearing) return
    setClearing(true)
    setError(null)
    try {
      await clearAccountingClientBookkeeping(client.client_unit_id)
      onCleared()
      onClose()
    } catch (clearError) {
      console.error('[ClearBookkeepingModal] Clear error:', clearError)
      setError(t('accounting.bookkeeping.clear.errors.run'))
    } finally {
      setClearing(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!clearing) onClose() }}
      title={t('accounting.bookkeeping.clear.title')}
      size="sm"
      onSubmit={handleClear}
      submitLabel={t('accounting.bookkeeping.clear.confirm')}
      submitting={clearing}
      closeLabel={t('actions.close')}
      showDefaultCloseButton={!clearing}
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          <p className="font-semibold">{t('accounting.bookkeeping.clear.warning')}</p>
          <p className="mt-2">{t('accounting.bookkeeping.clear.description', {
            client: client?.name || '',
          })}</p>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('accounting.bookkeeping.clear.memoryPreserved')}
        </p>
        {error && (
          <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
